import { Divider, Switch } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import ChevronRight from 'assets/svg/ChevronRight';
import PpaSplitSearchInput, { PPA_SPLIT_SEARCH_WIDTH } from 'components/molecules/PPA/PpaSplitSearchInput/PpaSplitSearchInput';
import type { SimulationFilterEditedAction, SimulationFilterFooterAction } from 'types/ppaSimulationMeta';
import type { PPAViewDataSource } from 'types/ppaViews';
import SimulatorFilterCategoryPanel, {
    type SimulatorFilterOption,
} from './SimulatorFilterCategoryPanel';
import {
    DEFAULT_SIMULATOR_FILTER_OPTIONS,
    EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    toSimulatorShowOnlyFilterItems,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';
import {
    PPA_SIM_FILTER_APPLY_BTN,
    PPA_SIM_FILTER_PANEL,
    PPA_SIM_FILTER_RESET_BTN,
    PPA_SIM_FILTER_SEARCH_INPUT,
    PPA_SIM_FILTER_SEARCH_SUBMIT,
    PPA_SIM_FILTER_SHOW_EDITED_BTN,
    PPA_SIM_FILTER_SHOW_EDITED_TOGGLE,
    PPA_SIM_FILTER_SHOW_IMPACTED_TOGGLE,
    PPA_SIM_FILTER_SHOW_IMPACTED_BTN,
    ppaSimFilterCategoryBtnTestId,
} from 'constants/ppaTestIds';
import { useSimulatorFilterEditedActions } from './useSimulatorFilterEditedActions';
import {
    useSimulatorFilterAttributeNames,
    type SimulatorFilterScopeItem,
} from './useSimulatorFilterAttributeNames';
import {
    StyledFilterApplyButton,
    StyledFilterPanelFooter,
    StyledFilterPanelFooterActions,
    StyledFilterPanelsRoot,
    StyledFilterResetButton,
    StyledPanel,
} from './SimulatorFilterPanel.styles';
import SearchEmptyResult from 'components/molecules/PricingSimulatorV3/SearchEmptyResult';

export type { SimulatorFilterOption, SimulatorFilterScopeItem, SimulatorShowOnlyState };

export type SimulatorAppliedFilters = {
    categorySelections: Record<string, string[]>;
    showOnly: SimulatorShowOnlyState;
};

export type SimulatorFilterCategory = {
    key: string;
    label: string;
    attributeType?: string;
    options?: SimulatorFilterOption[];
    dimension?: string;
    isDefaultSelected?: boolean;
};

type ShowOnlyLabels = {
    edited: string;
    impacted: string;
};

type Props = {
    filterCategories: SimulatorFilterCategory[];
    editedActionTypes?: SimulationFilterEditedAction[];
    scenarioId?: number | null;
    viewId?: number | null;
    viewDataSource?: PPAViewDataSource;
    isOpen?: boolean;
    appliedScope?: SimulatorFilterScopeItem[];
    appliedCategorySelections?: Record<string, string[]>;
    /** Committed show-only state — used for attribute-names API on category open. */
    appliedShowOnly?: SimulatorShowOnlyState;
    /** Meta defaults for draft toggles when nothing has been applied yet. */
    defaultShowOnly?: SimulatorShowOnlyState;
    showOnlyLabels?: ShowOnlyLabels;
    footerActions: SimulationFilterFooterAction[];
    isApplyLoading?: boolean;
    onApply?: (filters: SimulatorAppliedFilters) => void | Promise<boolean>;
};

function normalizeFooterActionId(id: string): string {
    return id.trim().toLowerCase().replace(/-/g, '_');
}

function hasAppliedShowOnly(state: SimulatorShowOnlyState): boolean {
    return state.edited || state.impacted || state.editedFilters.length > 0;
}

// @code-guard: skip-next-function reason="filter panel component — JSX layout + draft state logic; split into sub-components tracked in backlog"
const SimulatorFilterPanel = ({
    filterCategories = [],
    editedActionTypes = [],
    scenarioId = null,
    viewId = null,
    viewDataSource = 'view',
    isOpen = false,
    appliedScope,
    appliedCategorySelections = {},
    appliedShowOnly = EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    defaultShowOnly = EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    showOnlyLabels = { edited: 'Edited', impacted: 'Impacted' },
    footerActions,
    isApplyLoading = false,
    onApply,
}: Props) => {
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
    const [isEditedActionsOpen, setIsEditedActionsOpen] = useState(false);
    const [categorySelections, setCategorySelections] = useState<Record<string, string[]>>({});
    const [draftShowOnly, setDraftShowOnly] = useState<SimulatorShowOnlyState>(defaultShowOnly);
    const [searchQuery, setSearchQuery] = useState('');

    const { actionTypes: fetchedEditedActionTypes, isLoading: isEditedActionsLoading } =
        useSimulatorFilterEditedActions({
            viewId,
            scenarioId,
            dataSource: viewDataSource,
            enabled: isOpen && isEditedActionsOpen,
            fallbackActionTypes: editedActionTypes,
        });

    const resolvedEditedActionTypes = isEditedActionsOpen
        ? fetchedEditedActionTypes
        : editedActionTypes;

    const appliedCategorySelectionsRef = useRef(appliedCategorySelections);
    appliedCategorySelectionsRef.current = appliedCategorySelections;
    const appliedShowOnlyRef = useRef(appliedShowOnly);
    appliedShowOnlyRef.current = appliedShowOnly;
    const defaultShowOnlyRef = useRef(defaultShowOnly);
    defaultShowOnlyRef.current = defaultShowOnly;

    useEffect(() => {
        if (!isOpen) return;
        setCategorySelections(appliedCategorySelectionsRef.current);
        const applied = appliedShowOnlyRef.current;
        setDraftShowOnly(hasAppliedShowOnly(applied) ? applied : defaultShowOnlyRef.current);
        setSelectedCategoryKey(null);
        setIsEditedActionsOpen(false);
        setSearchQuery('');
    }, [isOpen]);

    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const isFilterSearchActive = normalizedSearchQuery.length > 0;

    const visibleFilterCategories = useMemo(() => {
        if (!isFilterSearchActive) return filterCategories;
        return filterCategories.filter((category) =>
            category.label.toLowerCase().includes(normalizedSearchQuery),
        );
    }, [filterCategories, isFilterSearchActive, normalizedSearchQuery]);

    const hasNoFilterSearchResults = isFilterSearchActive && visibleFilterCategories.length === 0;

    const selectedCategory = useMemo(
        () => filterCategories.find((category) => category.key === selectedCategoryKey) ?? null,
        [filterCategories, selectedCategoryKey],
    );

    const canExpandEditedActions = editedActionTypes.length > 0 || viewId != null;

    const editedActionOptions = useMemo(
        () =>
            resolvedEditedActionTypes.map((action) => ({
                id: action.input_value,
                label: action.label,
            })),
        [resolvedEditedActionTypes],
    );

    const selectedEditedActionValues = useMemo(
        () => draftShowOnly.editedFilters.map((filter) => filter.input_value),
        [draftShowOnly.editedFilters],
    );

    const { options: fetchedCategoryOptions, isLoading: isCategoryOptionsLoading } =
        useSimulatorFilterAttributeNames({
            category: selectedCategory,
            appliedScope,
            showOnly: appliedShowOnly,
            enabled: isOpen && selectedCategory != null,
        });

    const selectedCategoryOptions = useMemo(() => {
        if (fetchedCategoryOptions.length > 0) {
            return fetchedCategoryOptions;
        }
        if (selectedCategory?.options?.length) {
            return selectedCategory.options;
        }
        return isCategoryOptionsLoading ? [] : DEFAULT_SIMULATOR_FILTER_OPTIONS;
    }, [fetchedCategoryOptions, isCategoryOptionsLoading, selectedCategory]);

    const handleEditedToggle = (checked: boolean) => {
        if (!checked) {
            setDraftShowOnly({
                edited: false,
                impacted: false,
                editedFilters: [],
            });
            setIsEditedActionsOpen(false);
            return;
        }

        setDraftShowOnly((current) => ({
            edited: true,
            impacted: current.impacted,
            editedFilters: current.editedFilters,
        }));
    };

    const handleImpactedToggle = (checked: boolean) => {
        setDraftShowOnly((current) => ({
            edited: checked ? true : current.edited,
            impacted: checked,
            editedFilters: current.editedFilters,
        }));
    };

    const handleEditedActionSelectionChange = (selectedInputValues: string[]) => {
        setDraftShowOnly((current) => ({
            ...current,
            edited: true,
            editedFilters: toSimulatorShowOnlyFilterItems(
                resolvedEditedActionTypes,
                selectedInputValues,
            ),
        }));
    };

    const handleEditedRowClick = () => {
        if (!canExpandEditedActions) return;
        setSelectedCategoryKey(null);
        setIsEditedActionsOpen((current) => !current);
    };

    const handleCategoryClick = (categoryKey: string) => {
        setIsEditedActionsOpen(false);
        setSelectedCategoryKey((currentKey) => (currentKey === categoryKey ? null : categoryKey));
    };

    const handleFooterAction = async (actionId: string) => {
        const normalizedActionId = normalizeFooterActionId(actionId);

        if (normalizedActionId === 'reset') {
            setSelectedCategoryKey(null);
            setIsEditedActionsOpen(false);
            setCategorySelections({});
            setDraftShowOnly(defaultShowOnlyRef.current);
            return;
        }

        if (normalizedActionId === 'apply') {
            await onApply?.({
                categorySelections,
                showOnly: draftShowOnly,
            });
        }
    };

    const isCategoryActive = (categoryKey: string) => {
        const category = filterCategories.find((item) => item.key === categoryKey);
        const hasSelections = (categorySelections[categoryKey]?.length ?? 0) > 0;

        return hasSelections || Boolean(category?.isDefaultSelected);
    };

    const isEditedActionsActive =
        isEditedActionsOpen || draftShowOnly.editedFilters.length > 0 || draftShowOnly.edited;

    return (
        <StyledFilterPanelsRoot>
            {isEditedActionsOpen ? (
                <SimulatorFilterCategoryPanel
                    key="edited-actions"
                    categoryKey="edited_actions"
                    categoryLabel="Action types"
                    options={editedActionOptions}
                    isLoading={isEditedActionsLoading}
                    selectedOptionIds={selectedEditedActionValues}
                    onSelectedOptionIdsChange={handleEditedActionSelectionChange}
                    selectionDisabled={!draftShowOnly.edited}
                />
            ) : null}

            {selectedCategory ? (
                <SimulatorFilterCategoryPanel
                    key={selectedCategory.key}
                    categoryKey={selectedCategory.key}
                    categoryLabel={selectedCategory.label}
                    options={selectedCategoryOptions}
                    isLoading={isCategoryOptionsLoading}
                    selectedOptionIds={categorySelections[selectedCategory.key] ?? []}
                    onSelectedOptionIdsChange={(ids) =>
                        setCategorySelections((current) => ({
                            ...current,
                            [selectedCategory.key]: ids,
                        }))
                    }
                />
            ) : null}

            <StyledPanel data-testid={PPA_SIM_FILTER_PANEL}>
                <div className="filter-panel-section filter-panel-header">
                    <div className="filter-panel-title-wrap">
                        <p className="filter-panel-title">Filter</p>
                    </div>
                    <div className="filter-panel-search">
                        <PpaSplitSearchInput
                            width={PPA_SPLIT_SEARCH_WIDTH.FILTER_PANEL}
                            value={searchQuery}
                            onChange={setSearchQuery}
                            ariaLabel="Search filters"
                            submitAriaLabel="Submit filter search"
                            inputTestId={PPA_SIM_FILTER_SEARCH_INPUT}
                            submitTestId={PPA_SIM_FILTER_SEARCH_SUBMIT}
                        />
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-divider" />
                </div>

                <div className="filter-panel-show-only-scroll">
                    <div className="filter-panel-section filter-show-only-section">
                        <p className="filter-show-only-label">Show only</p>

                        <div
                            className={`filter-menu-item filter-show-only-item${
                                isEditedActionsActive ? ' active' : ''
                            }`}
                            data-testid={PPA_SIM_FILTER_SHOW_EDITED_BTN}
                            onClick={canExpandEditedActions ? handleEditedRowClick : undefined}
                        >
                            <div
                                className="filter-menu-item-left"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Switch
                                    size="small"
                                    checked={draftShowOnly.edited}
                                    onChange={handleEditedToggle}
                                    data-testid={PPA_SIM_FILTER_SHOW_EDITED_TOGGLE}
                                />
                                <span className="filter-show-only-item-label">
                                    {showOnlyLabels.edited}
                                </span>
                            </div>
                            {canExpandEditedActions ? (
                                <span className="filter-menu-chevron" aria-hidden>
                                    <ChevronRight />
                                </span>
                            ) : null}
                        </div>

                        <div
                            className="filter-menu-item filter-show-only-item"
                            data-testid={PPA_SIM_FILTER_SHOW_IMPACTED_BTN}
                        >
                            <div className="filter-menu-item-left">
                                <Switch
                                    size="small"
                                    checked={draftShowOnly.impacted}
                                    onChange={handleImpactedToggle}
                                    data-testid={PPA_SIM_FILTER_SHOW_IMPACTED_TOGGLE}
                                />
                                <span className="filter-show-only-item-label">
                                    {showOnlyLabels.impacted}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-inner-divider" />
                </div>

                <div className="filter-panel-categories-scroll">
                    <div className="filter-panel-section filter-categories-section">
                        {hasNoFilterSearchResults ? (
                            <SearchEmptyResult />
                        ) : (
                            visibleFilterCategories.map((category) => (
                                <div
                                    key={category.key}
                                    className={`filter-menu-item${
                                        isCategoryActive(category.key) ? ' active' : ''
                                    }`}
                                    data-testid={ppaSimFilterCategoryBtnTestId(category.key)}
                                    onClick={() => handleCategoryClick(category.key)}
                                >
                                    <span
                                        className={`filter-category-label${
                                            isCategoryActive(category.key)
                                                ? ' filter-category-label-active'
                                                : ''
                                        }`}
                                    >
                                        {category.label}
                                    </span>
                                    <span className="filter-menu-chevron" aria-hidden>
                                        <ChevronRight />
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="filter-panel-divider-wrap">
                    <Divider className="filter-panel-divider" />
                </div>

                <StyledFilterPanelFooter className="filter-panel-section">
                    <StyledFilterPanelFooterActions>
                        {footerActions.map((action) => {
                            const normalizedActionId = normalizeFooterActionId(action.id);
                            const isApply = normalizedActionId === 'apply';
                            const FooterButton = isApply
                                ? StyledFilterApplyButton
                                : StyledFilterResetButton;
                            const footerTestId = isApply
                                ? PPA_SIM_FILTER_APPLY_BTN
                                : PPA_SIM_FILTER_RESET_BTN;

                            return (
                                <FooterButton
                                    key={action.id}
                                    type={isApply ? 'primary' : undefined}
                                    disabled={action.isLocked || (isApply && isApplyLoading)}
                                    loading={isApply && isApplyLoading}
                                    data-testid={footerTestId}
                                    onClick={() => void handleFooterAction(action.id)}
                                >
                                    {action.label}
                                </FooterButton>
                            );
                        })}
                    </StyledFilterPanelFooterActions>
                </StyledFilterPanelFooter>
            </StyledPanel>
        </StyledFilterPanelsRoot>
    );
};

export default SimulatorFilterPanel;
